import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { parseCoordinates, yandexGeocode, yandexReverseGeocode } from 'helpers/yandexGeocoder'

// Поле поиска адреса по названию с выпадающими подсказками — через HTTP-геокодер Яндекса
// (см. helpers/yandexGeocoder): он находит адреса по тексту и сразу отдаёт координаты.
// Если вставили координаты — ищем не по названию, а по точке (обратное геокодирование).
// Область поиска ограничивается boundedBy (радиус доставки pos).
// Без ключа карт поле рендерится неактивным, чтобы все сети видели, что такой поиск есть.
function AddressSuggestField({ mapToken, boundedBy, disabled, placeholder, title, onPick, onOpenMap, openMapDisabled, className }) {
	const { t } = useTranslation()
	const [text, setText] = useState('')
	const [items, setItems] = useState([])
	const [open, setOpen] = useState(false)
	// Яндекс отклонил поиск (чаще всего — неверный ключ карт или не разрешён наш домен).
	// Раньше ошибка глоталась, и оператор видел просто пустой список
	const [searchFailed, setSearchFailed] = useState(false)
	// Яндекс ответил, но в зоне доставки ничего не нашёл — без подсказки это выглядело
	// так же, как неработающий ключ
	const [notFound, setNotFound] = useState(false)
	const timerRef = useRef(null)

	function handleChange(value) {
		setText(value)
		setSearchFailed(false)
		setNotFound(false)
		if (timerRef.current) clearTimeout(timerRef.current)
		const coordinates = parseCoordinates(value)
		// debounce + минимум 3 символа, чтобы не слать запрос на каждую букву
		if (!mapToken || (!coordinates && value.trim().length < 3)) {
			setItems([])
			setOpen(false)
			return
		}
		timerRef.current = setTimeout(async () => {
			try {
				// Введены координаты: адрес получаем обратным геокодированием, а сами координаты
				// оставляем ровно те, что вставили (точка не должна «прыгать» на дом).
				if (coordinates) {
					const label = `${coordinates.latitude}, ${coordinates.longitude}`
					const found = await yandexReverseGeocode(mapToken, coordinates.latitude, coordinates.longitude)
					setItems([{
						label: found ? `${found.full_address} (${label})` : label,
						latitude: coordinates.latitude,
						longitude: coordinates.longitude,
						address: found?.full_address || label,
						full_address: found?.full_address || label,
						short_address: found?.short_address || label,
					}])
					setOpen(true)
					return
				}

				const list = await yandexGeocode(mapToken, value, {
					results: 7,
					boundedBy: boundedBy || null,
					strictBounds: !!boundedBy,
				})
				setItems(list.map((item) => ({ ...item, label: item.full_address, address: item.full_address })))
				setOpen(true)
				setNotFound(list.length === 0)
			} catch (e) {
				setItems([])
				setOpen(true)
				setSearchFailed(true)
			}
		}, 400)
	}

	// Координаты и адрес уже получены при поиске — второй запрос не нужен.
	function handleSelect(item) {
		setItems([])
		setOpen(false)
		setText('')
		setNotFound(false)
		onPick({
			latitude: item.latitude,
			longitude: item.longitude,
			address: item.address,
			full_address: item.full_address,
			short_address: item.short_address,
		})
	}

	// «Не найдено» и отказ Яндекса показываем в том же выпадающем списке, что и подсказки:
	// строкой под полем они раздували обёртку, и кнопка карты (она по центру высоты) съезжала вниз
	const message = searchFailed ? t('address_search_failed') : (notFound ? t('address_not_found') : '')

	return (
		// без бутстраповского position-relative: у него !important, и он перебивал position: absolute
		// у .map-address-search — поле вставало над картой и сдвигало её на кнопки модалки
		<div className={`address-suggest ${className || ''}`}>
			<input type="text" className="form-control" autoComplete="off"
				disabled={disabled || !mapToken}
				placeholder={placeholder}
				title={mapToken ? title : t('map_token_not_connected')}
				value={text}
				onChange={(e) => handleChange(e.target.value)}
				onKeyDown={(e) => {
					// Enter выбирает первую подсказку — удобно сразу после вставки координат
					if (e.key === 'Enter') {
						e.preventDefault()
						if (items.length) handleSelect(items[0])
					}
				}}
				onFocus={() => { if (items.length || message) setOpen(true) }}
				onBlur={() => setTimeout(() => setOpen(false), 200)} />
			{onOpenMap &&
				<button type="button" className="input-button-right delivery"
					disabled={openMapDisabled}
					onClick={onOpenMap}>
					<i className="uil uil-map-marker input-icon"></i>
				</button>
			}
			{open && (items.length > 0 || message) &&
				<div className="address-suggest-dropdown">
					{items.map((item, index) => (
						<div key={index} className="address-suggest-item"
							onMouseDown={() => handleSelect(item)}>
							{item.label}
						</div>
					))}
					{!items.length &&
						<div className={`address-suggest-empty${searchFailed ? ' is-error' : ''}`}>
							<i className={`uil ${searchFailed ? 'uil-exclamation-triangle' : 'uil-search-alt'}`}></i>
							<span>{message}</span>
						</div>
					}
				</div>
			}
		</div>
	)
}

export default AddressSuggestField
